import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { isButtonField } from '@/features/records/utils/buttonField'
import { CONDITION_OPS, CONDITION_OP_LABELS, MAX_CONDITION_RULES, earlierValueFields, opNeedsValue, showIfOf, type ConditionOp, type ShowIf, type ShowIfRule } from '@/features/records/utils/fieldConditions'
import { HELP_TEXT_MAX, fieldWidth, helpText, isLayoutField } from '@/features/records/utils/layoutField'
import { isHiddenOnPublicForm } from '@/features/records/utils/prefill'
import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'

const UNIQUE_TYPES: FormFieldType[] = [
  FormFieldType.TEXT,
  FormFieldType.EMAIL,
  FormFieldType.PHONE,
  FormFieldType.NUMBER,
  FormFieldType.URL,
]

function optionsWith(field: FormField, changes: Record<string, unknown>) {
  return { ...(field.options ?? {}), ...changes }
}

function choiceList(field: FormField): string[] {
  if (!([FormFieldType.SELECT, FormFieldType.RADIO, FormFieldType.CHECKBOX] as string[]).includes(field.type)) return []
  const options = (field.options ?? {}) as Record<string, unknown>
  const choices = options.choices ?? options.values
  return Array.isArray(choices)
    ? choices.map((choice) => typeof choice === 'string' ? choice : String((choice as { value?: unknown }).value ?? '')).filter(Boolean)
    : []
}

function FieldWidthControl({ field, onUpdate }: { field: FormField; onUpdate: (id: string, updates: Partial<FormField>) => void }) {
  const selected = fieldWidth(field) ?? 'auto'
  return (
    <div className="space-y-1">
      <Label htmlFor={`${field.id}-width`} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Width</Label>
      <Select value={selected} onValueChange={(value) => onUpdate(field.id, { options: optionsWith(field, { width: value === 'auto' ? null : value }) })}>
        <SelectTrigger id={`${field.id}-width`} className="h-8"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Auto</SelectItem>
          <SelectItem value="half">Half</SelectItem>
          <SelectItem value="full">Full</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

function ShowIfEditor({ field, allFields, onUpdate }: {
  field: FormField
  allFields: FormField[]
  onUpdate: (id: string, updates: Partial<FormField>) => void
}) {
  const showIf = showIfOf(field)
  const candidates = earlierValueFields(field, allFields)
  const rules = showIf?.rules ?? []
  const save = (next: ShowIf | null) => onUpdate(field.id, { options: optionsWith(field, { showIf: next?.rules.length ? next : null }) })
  const setRules = (next: ShowIfRule[]) => save({ match: showIf?.match ?? 'all', rules: next })
  const change = (index: number, changes: Partial<ShowIfRule>) => setRules(rules.map((rule, i) => i === index ? { ...rule, ...changes } : rule))

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Show this field when</Label>
      {rules.length === 0 && <p className="text-xs text-muted-foreground">Always shown. Add a rule to show it only for some answers.</p>}
      {rules.length > 1 && (
        <Select value={showIf?.match ?? 'all'} onValueChange={(value) => save({ match: value === 'any' ? 'any' : 'all', rules })}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Match all rules</SelectItem><SelectItem value="any">Match any rule</SelectItem></SelectContent>
        </Select>
      )}
      {rules.map((rule, index) => {
        const target = candidates.find((candidate) => candidate.key === rule.fieldKey)
        const choices = target ? choiceList(target) : []
        return (
          <div key={`${field.id}-rule-${index}`} className="flex flex-wrap items-center gap-1.5" role="group" aria-label={`Rule ${index + 1}`}>
            <Select value={target ? rule.fieldKey : ''} onValueChange={(value) => change(index, { fieldKey: value, value: undefined })}>
              <SelectTrigger className="h-8 min-w-32 flex-1" aria-label="Field"><SelectValue placeholder="A field above…" /></SelectTrigger>
              <SelectContent>{candidates.map((candidate) => <SelectItem key={candidate.id} value={candidate.key}>{candidate.label || candidate.key}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={rule.op} onValueChange={(value) => change(index, { op: value as ConditionOp })}>
              <SelectTrigger className="h-8 min-w-28 flex-1" aria-label="Condition"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITION_OPS.map((op) => <SelectItem key={op} value={op}>{CONDITION_OP_LABELS[op]}</SelectItem>)}</SelectContent>
            </Select>
            {opNeedsValue(rule.op) && (choices.length > 0 ? (
              <Select value={rule.value ?? ''} onValueChange={(value) => change(index, { value })}>
                <SelectTrigger className="h-8 min-w-28 flex-1" aria-label="Value"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{choices.map((choice) => <SelectItem key={choice} value={choice}>{choice}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input
                value={rule.value ?? ''}
                aria-label="Value"
                placeholder={target?.type === FormFieldType.TOGGLE ? 'true or false' : 'Value'}
                onChange={(event) => change(index, { value: event.target.value })}
                className="h-8 min-w-24 flex-1"
              />
            ))}
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Remove rule" onClick={() => setRules(rules.filter((_, i) => i !== index))}>
              <X className="h-3.5 w-3.5" />
            </Button>
            {!target && <p className="w-full text-[11px] font-medium text-destructive">Choose a field above this one.</p>}
          </div>
        )
      })}
      {rules.length < MAX_CONDITION_RULES && candidates.length > 0 && (
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setRules([...rules, { fieldKey: candidates[candidates.length - 1].key, op: 'equals' }])}>
          <Plus className="mr-1 h-3 w-3" />Add rule
        </Button>
      )}
      {candidates.length === 0 && <p className="text-[11px] text-muted-foreground">Add a value field above this one to create a rule.</p>}
    </div>
  )
}

export function FieldBehaviorSettings({ field, allFields, onUpdate }: {
  field: FormField
  allFields: FormField[]
  onUpdate: (id: string, updates: Partial<FormField>) => void
}) {
  const validations = (field.validations ?? {}) as Record<string, unknown>
  const hasValue = !isButtonField(field) && !isLayoutField(field)
  const hiddenPublic = isHiddenOnPublicForm(field)

  return (
    <div className="space-y-3">
      {UNIQUE_TYPES.includes(field.type) && (
        <div className="border-t border-border pt-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={validations.unique === true}
              onCheckedChange={(unique) => {
                const next = { ...validations }
                if (unique) next.unique = true
                else delete next.unique
                onUpdate(field.id, { validations: Object.keys(next).length ? next : null })
              }}
              aria-label="No duplicates"
            />
            No duplicates
          </label>
          <p className="mt-1 text-[11px] text-muted-foreground">Reject values already used by another record in this group.</p>
        </div>
      )}
      {hasValue && (
        <div className="space-y-3 border-t border-border pt-3">
          <div className="space-y-1">
            <Label htmlFor={`${field.id}-help`} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Help text (optional)</Label>
            <Input
              id={`${field.id}-help`}
              value={helpText(field)}
              maxLength={HELP_TEXT_MAX}
              placeholder="Shown under the field"
              onChange={(event) => onUpdate(field.id, { options: optionsWith(field, { helpText: event.target.value || null }) })}
            />
            <p className="text-[11px] text-muted-foreground">Supports **bold**, *italic*, and [links](https://…).</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={hiddenPublic}
              disabled={field.required && !hiddenPublic}
              onCheckedChange={(hidden) => onUpdate(field.id, { options: optionsWith(field, { hideOnPublicForm: hidden ? true : null }) })}
              aria-label="Hide on the public form"
            />
            Hide on the public form
          </label>
          <p className="text-[11px] text-muted-foreground">
            {field.required && !hiddenPublic ? 'A required field cannot be hidden.' : 'A link can still prefill this field.'}
          </p>
          {field.type !== FormFieldType.PARAGRAPH && <FieldWidthControl field={field} onUpdate={onUpdate} />}
        </div>
      )}
      {!isButtonField(field) && <ShowIfEditor field={field} allFields={allFields} onUpdate={onUpdate} />}
    </div>
  )
}
